import 'regenerator-runtime/runtime'

import 'client/styles/main.global.css'
import 'client/styles/texts.global.css'
import 'client/styles/stripe.global.css'
import 'client/styles/typography.global.css'
import 'client/styles/print.global.css'

import { hydrateRoot } from 'react-dom/client'
import { createBrowserHistory } from 'history'
import getPageRoute, { resolvePageModule } from 'routes/ssr/get-page-route'
import AppRouter from 'routes/ssr/AppRouter'
import createStore from 'client/lib/create-store'
import { setPageError, constants as uiConstants } from 'client/store/ui/actions'
import { registerAuthInvalidatedHandler } from 'client/lib/auth-bridge'
import isModernBrowser from 'client/lib/is-modern-browser'
import ensureSafeMutationObserver from 'client/lib/ensure-safe-mutation-observer'
import { STORE_STATE_ID } from 'server/lib/inline-scripts'
import { init as initSentry } from 'client/lib/sentry'
import { init as initNewRelic } from 'client/lib/newrelic'
import { applyThemeOptIn } from 'client/lib/theme-opt-in'
import { Globals as ReactSpringGlobals } from 'react-spring'

// Initialize Sentry as early as possible
initSentry()
initNewRelic()
applyThemeOptIn()

// Accessibility-audit hook (no effect in normal use): when the a11y scanner sets this
// flag before the document loads, make react-spring jump every animation straight to its
// final value. The app fades content in (opacity 0→1) via react-spring, and the audit
// otherwise samples a mid-fade frame — reading text as a lightened composite over the page
// background and reporting false-positive color-contrast failures. Skipping the animation
// makes the scanned render deterministic.
if (typeof window !== 'undefined' && window.__A11Y_DISABLE_MOTION__) {
  ReactSpringGlobals.assign({ skipAnimation: true })
}
;(function () {
  const _error = console.error.bind(console)

  console.error = function (...args) {
    if (args?.[0]?.isAxiosError) {
      console.error(args[0].toJSON())
      return
    }

    _error(...args)
  }
})()

const loadPolyfills = async () => {
  if ('ResizeObserver' in window === false) {
    const module = await import('@juggle/resize-observer')
    window.ResizeObserver = module.ResizeObserver
  }
  ensureSafeMutationObserver()
  const isModern = isModernBrowser(navigator.userAgent)

  if (!isModern) {
    await import('core-js/stable')
  }
}

const main = async () => {
  await loadPolyfills()

  const history = createBrowserHistory()
  const { page } = await getPageRoute(history.location.pathname)
  const resolvedPage = await resolvePageModule(page)
  const initialAsyncReducers = resolvedPage.default?.asyncStores || {}

  const stateBlock = document.getElementById(STORE_STATE_ID)
  const initialStoreState = stateBlock ? JSON.parse(stateBlock.textContent) : undefined
  const { store } = createStore(initialStoreState, history, initialAsyncReducers)
  registerAuthInvalidatedHandler(() => store.dispatch({ type: uiConstants.LOGOUT_SUCCESS }))
  history.listen((location, action) => {
    setTimeout(() => {
      const state = store.getState()
      if (state.ui.pageError) {
        store.dispatch(setPageError())
      }
    })
  })

  hydrateRoot(document.getElementById('scite-app'), <AppRouter history={history} store={store} />)
}

if (!CONFIG.maintenance) {
  window.addEventListener('load', main)
}
